/**
 * Die Bildgeometrie der Bühne — an einer Stelle, weil zwei Ebenen sie brauchen.
 *
 * Die 3D-Szene rechnet den Versatz der Figur aus dem sichtbaren Bildausschnitt.
 * Das HUD ist reines SVG im Bildschirmraum und muss GENAU denselben Punkt
 * treffen, sonst liegen Ringe und Figur nicht übereinander. Zwei Kopien
 * derselben Formel wären genau die Sorte Duplikat, die beim nächsten
 * Kameraschwenk auseinanderläuft.
 *
 * Hier liegen ausserdem die POSEN: je Modul eine eigene Aufstellung der Figur.
 * Der Wechsel zwischen ihnen ist kein Schnitt, sondern eine gedämpfte Fahrt —
 * das ist der Übergang beim Klick auf eine andere Kachel.
 */

/** Kameraaufstellung. Wird von StageBackdrop an den Canvas gegeben. */
export const STAGE_CAMERA: {
  x: number
  y: number
  z: number
  fov: number
  near: number
  far: number
} = {
  x: 0,
  y: 0.5,
  z: 3.9,
  fov: 36,
  near: 0.1,
  far: 60,
}

/**
 * Feste Ansichten — die drei anatomischen Ebenen und die freie Kamera.
 *
 * WARUM SIE FEST SEIN MÜSSEN. Eine Bewegung in der Sagittalebene beurteilt man
 * von der Seite und nicht von schräg vorn; ein Winkel, den man aus einer
 * beliebigen Richtung ansieht, ist perspektivisch verkürzt und damit nicht
 * ablesbar. Die drei Ansichten stehen genau senkrecht auf ihrer Ebene.
 *
 * `azimut` und `hoehe` sind Kugelkoordinaten der Kamera um den Blickpunkt, in
 * Radiant. Die freie Ansicht hat keine — dort führt die Parallaxe.
 */
export type StageView = 'frei' | 'sagittal' | 'frontal' | 'transversal'

export const VIEWS: Record<Exclude<StageView, 'frei'>, { azimuth: number; elevation: number }> = {
  /* Von der rechten Körperseite: die Ebene, in der Gehen stattfindet. */
  sagittal: { azimuth: Math.PI / 2, elevation: 0 },
  /* Von vorn — Ab- und Anspreizung, Beckenobliquität. */
  frontal: { azimuth: 0, elevation: 0 },
  /* Von oben — Beckenrotation und Fussspur. */
  transversal: { azimuth: 0, elevation: Math.PI / 2 - 0.01 },
}

export const VIEW_LABELS: Record<StageView, { label: string; note: string }> = {
  frei: { label: 'Frei', note: 'Kamera folgt dem Zeiger' },
  sagittal: { label: 'Sagittal', note: 'von der Seite — Beugen und Strecken' },
  frontal: { label: 'Frontal', note: 'von vorn — Ab- und Anspreizung' },
  transversal: { label: 'Transversal', note: 'von oben — Drehung und Fussspur' },
}

/** Blickpunkt auf halber Figurenhöhe. */
export const FIGURE_TARGET_Y = 0.62

/** Kopfhöhe der Figur in Weltmetern — Blickpunkt der Porträtpose. */
export const FIGURE_HEAD_Y = 1.42

/** Eine Umdrehung dauert rund 80 Sekunden. */
export const ROTATION_SPEED = 0.078

/** Ab hier gilt das Fenster als breit und die Figur rückt aus der Mitte. */
const WIDE_ASPECT = 1.25

/**
 * Eine Aufstellung der Bühne.
 *
 * `offset` ist ein Anteil der halben Bildbreite, kein Weltmass. Das ist der
 * Punkt: so steht die Figur auf jedem Fenster an derselben RELATIVEN Stelle.
 * Ein fester Weltwert säße auf einem schmalen Fenster halb ausserhalb und auf
 * einem sehr breiten fast in der Mitte.
 */
export interface StagePose {
  /** Kurzname — steuert ausserdem, wann das HUD neu einrastet. */
  key: string
  /**
   * Versatz nach rechts, als Anteil der halben Bildbreite (0 … 0.9).
   *
   * Der Wert IST die Bildschirmstelle: `0.5 + offset / 2` der Fensterbreite.
   * Je enger die Pose, desto kleiner muss er sein — ein Portraet bei 0.78
   * schneidet den Kopf am rechten Rand ab, waehrend dieselbe Zahl bei der
   * Ganzfigur noch stimmt.
   */
  offset: number
  /** Abstand der Kamera zum Blickpunkt, in Weltmetern. */
  distance: number
  /** Höhe des Blickpunkts. */
  targetY: number
  /** Ruhehöhe der Kamera. */
  camY: number
  /** Grösse des HUD-Clusters, relativ zur Grundgrösse. */
  hud: number
  /** Beschriftung im Messwertblock. */
  label: string
}

/**
 * Die Posen.
 *
 * Der Gedanke dahinter: auf der Übersicht ist die Figur das Motiv, in den
 * datendichten Modulen ist sie Beiwerk und macht Platz, in der Verwaltung wird
 * sie zum Porträt — nah am Kopf, wie ein Profilbild. Dieselbe Figur, drei
 * Rollen.
 */
const POSES = {
  /** Übersicht: die Figur ist das Motiv. Ganzfigur, gross, rechts. */
  stage: {
    key: 'stage',
    offset: 0.74,
    distance: 3.9,
    targetY: FIGURE_TARGET_Y,
    camY: 0.5,
    hud: 1,
    label: 'Turntable',
  },
  /** Fachmodule: Listen brauchen Platz. Die Figur tritt zurück. */
  aside: {
    key: 'aside',
    offset: 0.72,
    distance: 5.4,
    targetY: 0.52,
    camY: 0.42,
    hud: 0.66,
    label: 'Beistand',
  },
  /** Bereiche: dazwischen. */
  half: {
    key: 'half',
    offset: 0.7,
    distance: 4.5,
    targetY: 0.58,
    camY: 0.48,
    hud: 0.84,
    label: 'Halbnah',
  },
  /** Verwaltung: Porträt. Nur Kopf und Schultern, wie ein Profilbild. */
  portrait: {
    key: 'portrait',
    offset: 0.58,
    distance: 1.5,
    targetY: FIGURE_HEAD_Y,
    camY: FIGURE_HEAD_Y + 0.06,
    hud: 0.46,
    label: 'Porträt',
  },
  /** Styleguide: die Seite zeigt Farbflächen. Die Figur hat dort nichts zu suchen. */
  distant: {
    key: 'distant',
    offset: 0.78,
    distance: 7.2,
    targetY: 0.46,
    camY: 0.44,
    hud: 0.5,
    label: 'Fern',
  },
} as const satisfies Record<string, StagePose>

/**
 * Die Pose des Easter Eggs.
 *
 * Sie steht ausserhalb von `POSES`, weil kein Pfad zu ihr führt: das Spiel ist
 * keine Seite. Die Figur rückt in die Bildmitte — sie ist hier weder Motiv noch
 * Beiwerk, sondern das, was man steuert, und dafür braucht sie Platz auf beiden
 * Seiten. Die Kamera steht tiefer und weiter zurück als sonst, damit ein
 * Hindernis früh genug ins Bild kommt, um darauf zu reagieren.
 */
export const DASH_POSE: StagePose = {
  key: 'dash',
  offset: 0.18,
  distance: 7.4,
  targetY: 0.94,
  camY: 1.28,
  hud: 0,
  label: 'Dash',
}

/**
 * Die Pose des Bühnenfensters.
 *
 * Anders als die Seitenposen ist sie MITTIG: das Fenster gehört niemandem als
 * Nachbarn, es hat seinen eigenen Rahmen. Der seitliche Versatz, der im
 * Hintergrund Platz für den Inhalt macht, wäre hier ein Fehler — er schöbe die
 * Figur an den Rand einer Karte, die nur für sie da ist.
 */
export const VIEWER_POSE: StagePose = {
  key: 'viewer',
  offset: 0,
  distance: 3.6,
  targetY: 0.92,
  camY: 1.0,
  hud: 0,
  label: 'Bühnenfenster',
}

export type PoseKey = keyof typeof POSES

/** Erster Pfadabschnitt → Pose. Reihenfolge ist egal, es wird exakt gematcht. */
/*
  Welche Seite welche Pose bekommt.

  DIE REGEL: je mehr Zahlen nebeneinander stehen müssen, desto weiter tritt die
  Figur zurück. Eine zehnspaltige Antriebstabelle neben einer halbhohen Figur
  ist keine Bühne, sondern eine abgeschnittene Tabelle.

  Die Figur verschwindet dabei nie ganz. Sie bleibt als Bezug im Bild — man soll
  jederzeit sehen, WOVON die Zahlen handeln.
*/
const BY_SEGMENT: Record<string, PoseKey> = {
  '': 'stage',

  /* Simulator: viel Text, mässig viele Zahlen. */
  modell: 'distant',
  parameter: 'aside',

  /* Simulator: Diagramme und breite Tabellen. */
  simulation: 'distant',
  analyse: 'distant',
  antriebe: 'distant',
  stueckliste: 'distant',

  /* Die angelegten Reiter zeigen bislang nur Text — dort darf die Figur nach vorn. */
  recorder: 'half',
  freigang: 'half',
  balance: 'half',
  dokumentation: 'aside',
  aufbau: 'aside',
  architektur: 'aside',
  konzept: 'aside',
  software: 'aside',
  markt: 'aside',
  praxis: 'aside',

  spaces: 'half',
  admin: 'portrait',
  styleguide: 'distant',
}

export function poseFor(pathname: string): StagePose {
  const segment = pathname.split('/')[1] ?? ''
  return POSES[BY_SEGMENT[segment] ?? 'stage']
}

/** Halbe sichtbare Breite in Weltmetern, auf Höhe des Blickpunkts. */
export function frameHalfWidth(aspect: number, distance = STAGE_CAMERA.z): number {
  const halfHeight = distance * Math.tan((STAGE_CAMERA.fov * Math.PI) / 360)
  return halfHeight * aspect
}

/** Versatz der Figur nach rechts, in Weltmetern. */
export function figureShift(aspect: number, pose: StagePose): number {
  if (aspect <= WIDE_ASPECT) return 0
  return frameHalfWidth(aspect, pose.distance) * pose.offset
}

/**
 * Dieselbe Stelle, nur als Anteil der Fensterbreite (0 = links, 1 = rechts).
 * Das ist der Wert, auf den das HUD seine Ringe zentriert.
 *
 * Weil `offset` bereits ein Anteil der halben Bildbreite ist, kürzt sich der
 * Abstand hier vollständig weg: die Figur steht in jeder Pose und auf jedem
 * Fenster an derselben Bildschirmstelle — sie wird nur grösser oder kleiner.
 */
export function figureScreenX(aspect: number, pose: StagePose): number {
  if (aspect <= WIDE_ASPECT) return 0.5
  return 0.5 + pose.offset * 0.5
}
